import React from 'react';
import { useContributionStatus } from '../../hooks/useSubmissions';

const StatusIcon = ({ hasSubmitted, hasDraft, isGM }) => {
  if (isGM) {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-600" title="Game Master">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </span>
    );
  }

  if (hasSubmitted) {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600" title="Submitted">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }

  if (hasDraft) {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-100 text-yellow-600" title="Drafting">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-400" title="Not started">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    </span>
  );
};

const OnlineIndicator = ({ isOnline }) => (
  <span
    className={`w-2 h-2 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`}
    title={isOnline ? 'Online' : 'Offline'}
  />
);

const AwardBadge = ({ isMVP, isGMPick }) => {
  if (!isMVP && !isGMPick) return null;

  return (
    <span className="flex items-center gap-0.5">
      {isMVP && (
        <span title="MVP" className="text-sm">🏆</span>
      )}
      {isGMPick && (
        <span title="GM Pick" className="text-sm">⭐</span>
      )}
    </span>
  );
};

const PlayerItem = ({ player, isGM, isOnline, isMVP, isGMPick }) => (
  <div className="flex items-center gap-2 py-1.5">
    <OnlineIndicator isOnline={isOnline} />
    <StatusIcon
      hasSubmitted={player.hasSubmitted}
      hasDraft={player.hasDraft}
      isGM={isGM}
    />
    <span className={`text-sm ${player.hasSubmitted ? 'text-gray-900' : 'text-gray-600'}`}>
      {player.displayName}
    </span>
    <AwardBadge isMVP={isMVP} isGMPick={isGMPick} />
  </div>
);

export const PlayerContributionList = ({
  gameId,
  roundId,
  showDuringPhases = ['submission'],
  onlineUserIds = [],
  mvpUserId,
  gmPickUserId
}) => {
  const { data, isLoading } = useContributionStatus(gameId, roundId);

  // Convert to Set for O(1) lookups
  const onlineSet = new Set(onlineUserIds.map(id => id.toString()));

  if (isLoading || !data) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
        <div className="h-6 bg-gray-200 rounded w-full"></div>
        <div className="h-6 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  const { phase, gm, players, summary } = data;
  const showStatus = showDuringPhases.includes(phase);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Players</h3>
        {showStatus && summary && (
          <span className="text-xs text-gray-500">
            {summary.submitted}/{summary.total} submitted
          </span>
        )}
      </div>

      <div className="space-y-0.5">
        {/* GM always listed first */}
        <PlayerItem
          player={gm}
          isGM={true}
          isOnline={onlineSet.has(gm.id?.toString())}
          isMVP={mvpUserId === gm.id?.toString()}
          isGMPick={false}
        />

        {/* Players */}
        {players.map(player => (
          <PlayerItem
            key={player.id}
            player={player}
            isGM={false}
            isOnline={onlineSet.has(player.id?.toString())}
            isMVP={mvpUserId === player.id?.toString()}
            isGMPick={gmPickUserId === player.id?.toString()}
          />
        ))}
      </div>

      {/* Progress bar during submission phase */}
      {showStatus && summary && summary.total > 0 && (
        <div className="mt-3">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${(summary.submitted / summary.total) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
